/**
 * 🎒 What a player is carrying, and the rules for putting something into it.
 *
 * **Pure and storage-free, deliberately.** The server owns the rows; this owns
 * the *rules*, so stacking, overflow and "am I full" can be tested without a
 * database and answered identically on both sides of the wire.
 *
 * ## The shape haugur chose, 2026-08-17
 *
 * 🎯 *"i think we go 10 on hand and 24 in invetory, players will need that
 * space, gater for kaupskip etc etc"* — a **kaupskip** is a merchant ship.
 *
 * | | |
 * |---|---|
 * | **Slots 0–9** | The **on-hand** row across the bottom of the screen |
 * | **Slots 10–33** | The **pack**, seen only when the inventory is open |
 *
 * One numbering rather than two lists, because an item moves between them
 * constantly and the move should not change what kind of thing it is.
 *
 * ## 🔴 The rule that decides where a picked-up block goes
 *
 * 🎯 **haugur:** *"a 10 block on hand inventory, when that is full everything you
 * pick up goes to regular inventory."*
 *
 * So the order is: **top up a stack you already have, on hand first, then in the
 * pack; failing that, take an empty slot, on hand first, then in the pack.**
 * Topping up before opening a new slot is what stops ten separate stacks of
 * three móberg appearing when one slot would hold them all.
 *
 * ## ⚠️ And when there is no room at all
 *
 * 🎯 **haugur, asked directly:** the block **stays in the ground**. You cannot
 * mine any more until you make room. **It does not drop on the floor** —
 * Minecraft drops it, and a cube you cannot pick up looks broken.
 *
 * That is why {@link addToInventory} reports what it could NOT fit rather than
 * silently discarding it: the caller has to refuse the break.
 */

import { TUNING } from './tuning.ts';
import { itemStackLimit, wornAt, type BodyPart } from './items.ts';

/** Slots 0…9. The row across the bottom of the screen. */
export const INVENTORY_ON_HAND = 10;

/** Slots 10…33. Seen only when the inventory is open. */
export const INVENTORY_PACK = 24;

/**
 * 34 — **everything a pickup may fill.** The ten on hand plus the pack.
 *
 * 🔴 **This is the bound {@link addToInventory} walks, and it is NOT
 * {@link INVENTORY_SLOTS}.** The clothing slots come after it in the same
 * numbering, and a mined block that landed in one of them would be a lump of
 * turf worn as a hat. **The two numbers were one number until 2026-08-19**, and
 * separating them is the whole of what stops that.
 *
 * ⚠️ **Deliberately not in the database.** Carrying capacity is meant to grow —
 * a **poki** or a **skjatta** as craftable progression — and a number that lives
 * in one file grows without a migration. `migrations/013_inventory.sql` checks
 * only `slot >= 0` for exactly this reason, which is why growing to 38 needed no
 * migration at all.
 */
export const INVENTORY_CARRIED = INVENTORY_ON_HAND + INVENTORY_PACK;

/**
 * 👕 **What is on your body, in the order the slots are drawn** (T153).
 *
 * 🎯 **haugur, 2026-08-19**, with a picture of Minecraft's inventory: *"we need
 * that way we can clothe parts of the body, i can put on a hat, or a sweater or
 * better shoes."* ✅ **He confirmed the four and their names the same day.**
 *
 * ⚠️ **Order is meaning here** — slot 34 is the head and slot 37 the feet — so
 * this array must never be reordered. It is head-downwards, which is how the
 * slots are drawn and how anybody dresses.
 */
export const CLOTHING_ORDER: readonly BodyPart[] = ['head', 'body', 'legs', 'feet'];

/** 4. Slots 34–37, one per part of the body. */
export const INVENTORY_CLOTHING = CLOTHING_ORDER.length;

/**
 * 38 — every slot that exists, carried and worn together.
 *
 * ⚠️ **One numbering across all three places**, so moving a garment out of the
 * pack and onto your head is the same message as any other move, with no
 * special case on the wire.
 */
export const INVENTORY_SLOTS = INVENTORY_CARRIED + INVENTORY_CLOTHING;

/** One occupied slot. An empty slot is an **absent entry**, never a zero. */
export interface Stack {
  readonly slot: number;
  readonly itemKey: string;
  readonly count: number;
  /**
   * 🔨 **How many blocks this tool has already broken** (D104). Absent means
   * none, which is also what every non-tool means.
   *
   * 🔴 **This is the ONLY per-slot state in the game**, and it is why a tool has
   * `stack: 1`: two tools in one slot cannot carry two different amounts of
   * wear. `Minecraft/09` §2.2 states the same rule.
   *
   * ⚠️ **Optional on purpose.** Every row written before 2026-09-01 has no
   * value, and *no wear* is the right reading of that — so the migration's
   * default and this field's absence say the same thing.
   */
  readonly used?: number;
}

/** What happened when something was picked up. */
export interface AddResult {
  /** The pack as it now stands. A new array; the input is never modified. */
  readonly stacks: readonly Stack[];
  /**
   * 🔴 **How many could NOT be fitted.** Zero means everything went in.
   *
   * ⚠️ **The caller must check this.** A break that cannot be carried has to be
   * refused, or the block is destroyed and the item lost — which is exactly the
   * silent-loss failure this project keeps being bitten by.
   */
  readonly leftOver: number;
}

/**
 * How many of one item fit in a single slot.
 *
 * 🔴 **It depends on the ITEM since 2026-09-01, and it has to.** A tool is
 * `stack: 1` because it carries how worn it is (D104) — ask without a key and
 * you get the game-wide default, which would let six axes with six different
 * amounts of wear left pile into one slot with one number between them.
 *
 * ⚠️ **An unknown key falls back to the default**, because a name nobody
 * recognises is not a tool and stacking it normally is the harmless answer.
 */
export function stackLimit(itemKey?: string): number {
  const own = itemKey === undefined ? undefined : itemStackLimit(itemKey);
  return own ?? TUNING.inventory.defaultStackSize;
}

/** Is this slot number a real slot at all? */
export function isValidSlot(slot: number): boolean {
  return Number.isInteger(slot) && slot >= 0 && slot < INVENTORY_SLOTS;
}

/** Is this slot on hand, rather than in the pack? */
export function isOnHand(slot: number): boolean {
  return isValidSlot(slot) && slot < INVENTORY_ON_HAND;
}

/** 👕 Is this one of the four slots on your body? */
export function isClothingSlot(slot: number): boolean {
  return isValidSlot(slot) && slot >= INVENTORY_CARRIED;
}

/**
 * 👕 Which part of the body this slot dresses, or `undefined` for a carried one.
 */
export function bodyPartOfSlot(slot: number): BodyPart | undefined {
  if (!isClothingSlot(slot)) return undefined;
  return CLOTHING_ORDER[slot - INVENTORY_CARRIED];
}

/**
 * 👕 **May this item go in this slot?**
 *
 * Every carried slot takes anything. A clothing slot takes **only something worn
 * on that part of the body**, and only one of it — you wear one hat.
 *
 * ⚠️ **Today this refuses everything for the four clothing slots**, because no
 * item declares where it is worn yet. That is correct rather than broken: the
 * garments arrive with the cold system that gives them a job (**T131**).
 */
export function canGoIn(slot: number, itemKey: string, count = 1): boolean {
  if (!isValidSlot(slot)) return false;
  if (!isClothingSlot(slot)) return true;
  return count === 1 && wornAt(itemKey) === bodyPartOfSlot(slot);
}

/**
 * Put `count` of an item into the pack.
 *
 * **On-hand first, and top up before opening a new slot** — see the note at the
 * top of this file for why that order and not another.
 *
 * ⚠️ **Never mutates its input**, so the caller can compare before and after and
 * write only the rows that changed. Rewriting all 34 is the fault this whole
 * table exists to avoid.
 */
export function addToInventory(
  stacks: readonly Stack[],
  itemKey: string,
  count: number,
): AddResult {
  if (count <= 0) return { stacks: [...stacks], leftOver: 0 };

  const limit = stackLimit(itemKey);
  const bySlot = new Map<number, Stack>(stacks.map((s) => [s.slot, s]));
  let left = count;

  /* ① Top up stacks of the same item that already exist, lowest slot first —
        so the on-hand row fills before the pack without a second rule. */
  const existing = [...bySlot.values()]
    /* ⚠️ Carried slots only. A clothing slot holds one worn thing and is not a
       place a pickup may quietly add to — same reason as the loop below. */
    .filter((s) => !isClothingSlot(s.slot) && s.itemKey === itemKey && s.count < limit)
    .sort((a, b) => a.slot - b.slot);
  for (const stack of existing) {
    if (left === 0) break;
    const room = limit - stack.count;
    const moved = Math.min(room, left);
    bySlot.set(stack.slot, { ...stack, count: stack.count + moved });
    left -= moved;
  }

  /* ② Then empty slots, again lowest first.
        🔴 **`INVENTORY_CARRIED`, never `INVENTORY_SLOTS`.** The four clothing
        slots sit above the pack in the same numbering, and this loop is what
        would otherwise post a mined lump of móberg straight onto your head. */
  for (let slot = 0; slot < INVENTORY_CARRIED && left > 0; slot++) {
    if (bySlot.has(slot)) continue;
    const moved = Math.min(limit, left);
    bySlot.set(slot, { slot, itemKey, count: moved });
    left -= moved;
  }

  return {
    stacks: [...bySlot.values()].sort((a, b) => a.slot - b.slot),
    leftOver: left,
  };
}

/**
 * 🔴 **Could this player pick up one more of this item?**
 *
 * The question the server asks **before** letting a block break, because the
 * answer decides whether the block stays in the ground.
 */
export function hasRoomFor(stacks: readonly Stack[], itemKey: string, count = 1): boolean {
  return addToInventory(stacks, itemKey, count).leftOver === 0;
}

/**
 * 🎒 **Is there room for ALL of these together?** — each added to the pack the
 * one before it left, so two things that each fit the last free slot do NOT
 * both fit it. 🔴 Asking {@link hasRoomFor} once per entry against the same
 * pack said yes to both, and `intoPack` then dropped what did not fit — which
 * taking down a pit house (its item, the oven's wood, the bench's wool and
 * thread) made the common case (D135, review finding 3).
 */
export function hasRoomForAll(
  stacks: readonly Stack[],
  entries: readonly { readonly itemKey: string; readonly count: number }[],
): boolean {
  let trial = stacks;
  for (const e of entries) {
    const added = addToInventory(trial, e.itemKey, e.count);
    if (added.leftOver > 0) return false;
    trial = added.stacks;
  }
  return true;
}

/** How many of one item are being carried, across every slot. */
export function countOf(stacks: readonly Stack[], itemKey: string): number {
  return stacks.reduce((n, s) => (s.itemKey === itemKey ? n + s.count : n), 0);
}

/** What happened when something was taken out of the pack. */
export interface TakeResult {
  /** The pack as it now stands. A new array; the input is never modified. */
  readonly stacks: readonly Stack[];
  /**
   * ⚠️ **How many could NOT be found.** `0` means the whole amount was taken.
   *
   * 🔴 **The caller must check it.** A partial take that is treated as a whole
   * one destroys a player's materials and gives nothing back, which is the one
   * failure crafting can have that nobody can undo.
   */
  readonly shortBy: number;
}

/**
 * 🔨 **Take `count` of an item out of the pack** — the other half of
 * {@link addToInventory}, added for crafting (T130 slice 3).
 *
 * 🔴 **HIGHEST SLOT FIRST, and that is a decision rather than an accident.**
 * The pack is emptied before the on-hand row, so **what you are holding
 * survives longest**. Taking from slot 0 downwards would quietly empty the hand
 * you are working with, and the player would watch their tool vanish while
 * looking at a recipe.
 *
 * ⚠️ **Clothing slots are never touched.** What you are wearing is not
 * material, for the same reason {@link addToInventory} will not fill those
 * slots.
 */
export function takeFromInventory(
  stacks: readonly Stack[],
  itemKey: string,
  count: number,
): TakeResult {
  if (count <= 0) return { stacks: [...stacks], shortBy: 0 };

  let left = count;
  const out: Stack[] = [];
  /* Highest slot first — see the note above. */
  const order = [...stacks].sort((a, b) => b.slot - a.slot);

  for (const stack of order) {
    if (left === 0 || stack.itemKey !== itemKey || isClothingSlot(stack.slot)) {
      out.push(stack);
      continue;
    }
    const taken = Math.min(stack.count, left);
    left -= taken;
    /* A stack taken to nothing leaves the pack entirely rather than sitting
       there as a zero, which is how every other path in this file represents
       an empty slot. */
    if (stack.count - taken > 0) out.push({ ...stack, count: stack.count - taken });
  }

  return { stacks: out.sort((a, b) => a.slot - b.slot), shortBy: left };
}

/** What happened when one slot was moved onto another. */
export interface MoveResult {
  /** The pack as it now stands. A new array; the input is never modified. */
  readonly stacks: readonly Stack[];
  /**
   * Whether anything actually changed.
   *
   * ⚠️ **`false` is not an error.** It is the answer to a move that is not
   * allowed — an empty source, a full destination, half a stack onto a
   * different item — and the caller's job is to send the inventory back
   * unchanged so the screen stops disagreeing with the server.
   */
  readonly moved: boolean;
}

/**
 * 🔴 **Move `count` of what is in `from` into `to`.** The first rule in this
 * file that REARRANGES rather than adds.
 *
 * ## The four cases, and they are the whole design
 *
 * | `to` holds | What happens |
 * |---|---|
 * | Nothing | The items move. Taking fewer than all of them is how a stack **splits** |
 * | **The same item** | Topped up to the stack limit; anything that will not fit **stays in `from`** |
 * | **A different item** | The two slots **swap** — but only when `count` is the whole of `from` |
 * | The same item, already full | Nothing. `moved` is `false` |
 *
 * ⚠️ **Half a stack cannot be swapped**, and refusing it is deliberate rather
 * than lazy. There is nowhere for the other half to go: the destination's items
 * would have to land in a slot nobody chose, which is the "where did my things
 * go" failure the pickup rules at the top of this file already exist to avoid.
 *
 * ## 🔴 Why there is no *held* item here
 *
 * Minecraft carries a stack on the cursor, so an item can be **nowhere** — in no
 * slot, in no container, in the air. That is a third place for things to live,
 * it has to survive a disconnection, and it is the part of the interface that
 * took Mojang four years (`Development/Minecraft/09` §2.3).
 *
 * **Ours never leaves its slot until it arrives in another one.** Picking up is
 * a drawing the client does; only the finished move is sent. So a connection
 * that drops mid-drag loses nothing, because as far as the server is concerned
 * nothing had begun. ⚠️ **It also means the client's picture can be out of date**
 * — hence `moved: false` rather than a clamp: the server refuses what it does
 * not recognise and says what is really there.
 */
export function moveStack(
  stacks: readonly Stack[],
  from: number,
  to: number,
  count: number,
): MoveResult {
  const unchanged: MoveResult = { stacks: [...stacks], moved: false };

  if (!isValidSlot(from) || !isValidSlot(to) || from === to) return unchanged;
  if (!Number.isInteger(count) || count <= 0) return unchanged;

  const bySlot = new Map<number, Stack>(stacks.map((s) => [s.slot, s]));
  const source = bySlot.get(from);
  if (source === undefined) return unchanged;

  /* ⚠️ Not clamped. A client asking to move more than is there is a client
     working from a stale inventory, and quietly moving "as many as we have"
     would hide that rather than correct it. */
  if (count > source.count) return unchanged;

  /* 👕 The destination decides what it will take (T153). A clothing slot accepts
     only a garment for that part of the body, and only one of it — so a lump of
     móberg cannot be worn, and a hat cannot be worn on your feet. ⚠️ Refusing
     rather than clamping, exactly as everywhere else in this function. */
  if (!canGoIn(to, source.itemKey, count)) return unchanged;

  const limit = stackLimit(source.itemKey);
  const target = bySlot.get(to);
  const remainder = source.count - count;

  const put = (slot: number, stack: Stack | undefined): void => {
    if (stack === undefined) bySlot.delete(slot);
    else bySlot.set(slot, stack);
  };

  if (target === undefined) {
    put(from, remainder > 0 ? { ...source, count: remainder } : undefined);
    put(to, { ...source, slot: to, count });
  } else if (target.itemKey === source.itemKey) {
    const room = limit - target.count;
    if (room <= 0) return unchanged;
    const fits = Math.min(room, count);
    put(from, source.count - fits > 0 ? { ...source, count: source.count - fits } : undefined);
    put(to, { ...target, count: target.count + fits });
  } else {
    /* Two different items: a swap, and only ever a whole one. */
    if (remainder > 0) return unchanged;
    /* 👕 And the displaced item has to be allowed where it is going, which
       matters when the source is itself a clothing slot — taking a hat off by
       swapping a stone into its place must not work either way round. */
    if (!canGoIn(from, target.itemKey, target.count)) return unchanged;
    put(from, { ...target, slot: from });
    put(to, { ...source, slot: to });
  }

  return { stacks: [...bySlot.values()].sort((a, b) => a.slot - b.slot), moved: true };
}

/** What happened when something was thrown away. */
export interface DiscardResult {
  /** The pack as it now stands. A new array; the input is never modified. */
  readonly stacks: readonly Stack[];
  /** How many were actually thrown away. **Zero means nothing changed.** */
  readonly discarded: number;
}

/**
 * 🗑️ **Throw some of a slot away. It is gone.**
 *
 * 🎯 **haugur, 2026-08-19**, choosing between three ways to get rid of
 * something: *"ok 1 is fine for now"* — destroyed, rather than dropped on the
 * ground or put back into the world.
 *
 * 🔴 **Why it cannot be a drop.** This game has **no entities**: nothing exists
 * in the world except blocks, so there is nowhere for a dropped thing to land.
 * And haugur ruled on the same question once already — a broken block with a
 * full pack **stays in the ground** rather than dropping, because *"a cube you
 * cannot pick up looks broken"*. Building the first entity to hold rubbish would
 * be the largest possible answer to the smallest version of the question.
 *
 * ## ⚠️ There is no undo, so the safety is in the GESTURE
 *
 * Nothing here can be reversed, and **D21 already takes everything you carry
 * when you die** — so a mis-click that costs a stack is a real loss, not an
 * annoyance. The protection is not in this function: it is that throwing
 * something away takes **two deliberate actions** — pick the stack up, then put
 * it on a mark that sits well away from every slot — and that the game **says
 * what it threw away** rather than doing it in silence.
 *
 * ⚠️ **Not clamped**, like every other rule in this file: asking to throw away
 * more than is there throws away nothing and reports it, so the caller can send
 * the pack back rather than quietly destroying a different amount.
 */
export function discardFrom(stacks: readonly Stack[], slot: number, count: number): DiscardResult {
  const unchanged: DiscardResult = { stacks: [...stacks], discarded: 0 };

  if (!isValidSlot(slot)) return unchanged;
  if (!Number.isInteger(count) || count <= 0) return unchanged;

  const source = stacks.find((stack) => stack.slot === slot);
  if (source === undefined || count > source.count) return unchanged;

  const left = source.count - count;
  const after = stacks.flatMap((stack) =>
    stack.slot !== slot ? [stack] : left > 0 ? [{ ...stack, count: left }] : [],
  );

  return { stacks: after, discarded: count };
}
